/**
 * What to do when the service worker controlling this page changes
 * (UI-audit a4-08). Dependency-free so test/sw-update.test.ts can load it.
 *
 * `sw.js` calls `clients.claim()` on activate, so the very first install also
 * fires `controllerchange` — and by the time it does, `controller` is already
 * set, which made main.tsx's old `!controller` guard dead: every first visit
 * reloaded once, mid-sign-in included. The question that tells an update from
 * a first install is whether the page was controlled *before* the change, so
 * that is what the caller tracks and passes in as `wasControlled`.
 *
 * `busy` is a separate question with a separate answer: the page was running
 * old code and should reload, but someone is typing or has a sheet up, and a
 * reload now would throw that away. Then the reload waits (`defer`) — the
 * caller offers it in a toast and tries again on the next foreground. A cold
 * start loads the new build anyway (navigations are network-first in sw.js),
 * so deferring never strands a phone on an old bundle.
 */
export type ControllerChange = 'ignore' | 'reload' | 'defer'

export function onControllerChange(s: {
  /** A worker controlled this page before this change. */
  wasControlled: boolean
  /** There is a controller now (null when the worker went redundant). */
  hasController: boolean
  /** A reload is already under way. */
  reloading: boolean
  busy: boolean
}): ControllerChange {
  if (s.reloading || !s.hasController || !s.wasControlled) return 'ignore'
  return s.busy ? 'defer' : 'reload'
}

/**
 * Would a reload right now lose something? A focused field (`typing` — see
 * `isTypingTarget`), an open sheet or dialog (Vaul's content carries
 * `role="dialog"`), or a textarea holding text that has not been sent — the
 * Ask composer, a note — whether or not it still has the focus. A text input
 * with a value is not counted: Browse's search box keeps its query for good,
 * and counting it would defer every update forever.
 */
export function isBusy(s: { typing: boolean; dialogOpen: boolean; unsentText: boolean }): boolean {
  return s.typing || s.dialogOpen || s.unsentText
}
